import type {Message, UiBlock} from '../service/chat.service';
import {normalizeMarkdown} from '../../../pipes/normalize-markdown.pipe';

/**
 * Titel einer Zusammenfassung: die Frage des Blocks, sonst der Name aus den Aufzählungspunkten
 * („Name: ASR Prüfung"), sonst „Zusammenfassung". Gilt für die Anzeige und das Kopieren gleichermaßen.
 */
export function summaryHeaderTitle(uiBlock: UiBlock | undefined): string {
  const question = uiBlock?.question?.trim();
  if (question) {
    return question;
  }

  const nameBulletPoint = (uiBlock?.summaryFields ?? [])
    .flatMap(field => field.bulletPoints)
    .find(point => /^name\s*:/i.test(point.trim()));

  if (nameBulletPoint) {
    return nameBulletPoint.replace(/^name\s*:\s*/i, '').trim();
  }

  return 'Zusammenfassung';
}

/**
 * Die myCheck-Zusammenfassung als Markdown: Titel als Überschrift, je Abschnitt eine Unterüberschrift
 * mit Aufzählung. Eine Freitext-Zusammenfassung (ohne Abschnitte) wird unverändert darunter gesetzt.
 * Leer, wenn der Block nichts zusammenfasst.
 */
export function summaryToMarkdown(uiBlock: UiBlock): string {
  const fields = uiBlock.summaryFields ?? [];
  const freeText = uiBlock.summary?.trim() ?? '';
  if (fields.length === 0 && !freeText) {
    return '';
  }

  const parts = [`## ${summaryHeaderTitle(uiBlock)}`];
  if (fields.length > 0) {
    for (const field of fields) {
      const bullets = field.bulletPoints.map(point => `- ${point.trim()}`);
      parts.push([`### ${field.title.trim()}`, ...bullets].join('\n'));
    }
  } else {
    parts.push(normalizeMarkdown(freeText));
  }
  return parts.join('\n\n');
}

/**
 * Was „Kopieren" an einer Nachricht in die Ablage legt: der Nachrichtentext und, wie angezeigt, die
 * Zusammenfassungen der Nachricht als Markdown. Eine Nachricht ohne Zusammenfassung ergibt unverändert
 * ihren Text.
 */
export function messageCopyText(message: Message): string {
  const summaries = (message.uiBlock ?? [])
    .filter(uiBlock => uiBlock.type === 'summary')
    .map(summaryToMarkdown)
    .filter(markdown => markdown.length > 0);
  if (summaries.length === 0) {
    return message.message || '';
  }
  return [message.message?.trim(), ...summaries].filter(Boolean).join('\n\n');
}
